// Fadenkreuz, Wertschild und Trefferflächen.
//
// Das Schild hängt am Punkt auf der Linie und zeigt nur die eine Zahl, um die es
// geht. Alles Weitere steht im Kopf der Karte. Ein vollständiger Tooltip am
// Cursor würde Daten verdecken, springen und auf Touch nicht funktionieren.
//
// Die Schicht meldet nur, worüber der Zeiger steht; die Textausgabe im Kopf
// passiert in React, damit beim Hovern nicht das ganze SVG neu gebaut wird.

import * as d3 from "d3";

import { PALETTE } from "../../palette";
import { pct } from "../../format";
import type { Day, G, Hover, Model } from "../model";

export type HoverHandler = (h: Hover) => void;

const CHIP_PAD_X = 6;
const CHIP_PAD_Y = 3;
const CHIP_GAP = 9;

export function drawHover(g: G, model: Model, lang: string, onHover: HoverHandler): void {
    const { marginLeft, innerW, plotTop, axisY, split, histW, fwdW } = model.layout;
    const rightEdge = marginLeft + innerW;

    const cross = g
        .append("line")
        .attr("y1", plotTop)
        .attr("y2", axisY)
        .attr("stroke", PALETTE.ink)
        .attr("stroke-width", 0.8)
        .attr("opacity", 0);

    const dot = g.append("circle").attr("r", 4).attr("fill", PALETTE.ink).attr("opacity", 0);

    const chip = g.append("g").attr("opacity", 0);
    const chipBg = chip
        .append("rect")
        .attr("rx", 3)
        .attr("fill", PALETTE.bg)
        .attr("stroke", "rgba(245, 245, 245, 0.25)")
        .attr("stroke-width", 1);
    const chipText = chip
        .append("text")
        .attr("font-size", 11)
        .attr("fill", PALETTE.ink)
        .attr("dominant-baseline", "middle");

    function place(px: number, py: number, label: string): void {
        chipText.text(label);
        const w = (chipText.node() as SVGTextElement).getComputedTextLength();
        const boxW = w + CHIP_PAD_X * 2;
        const boxH = 11 + CHIP_PAD_Y * 2;

        // rechts vom Punkt, sofern noch Platz ist; sonst nach links klappen
        const left = px + CHIP_GAP + boxW <= rightEdge ? px + CHIP_GAP : px - CHIP_GAP - boxW;
        const top = Math.max(plotTop, Math.min(axisY - boxH, py - boxH / 2));

        chipBg.attr("x", left).attr("y", top).attr("width", boxW).attr("height", boxH);
        chipText.attr("x", left + CHIP_PAD_X).attr("y", top + boxH / 2 + 0.5);
        chip.attr("opacity", 1);
    }

    const clear = () => {
        cross.attr("opacity", 0);
        dot.attr("opacity", 0);
        chip.attr("opacity", 0);
        onHover(null);
    };

    const bisect = d3.bisector<Day, Date>((d) => d.date).center;

    g.append("rect")
        .attr("x", marginLeft)
        .attr("y", plotTop)
        .attr("width", histW)
        .attr("height", axisY - plotTop)
        .attr("fill", "transparent")
        .style("cursor", "crosshair")
        .on("mousemove touchmove", function (event: any) {
            const mx = d3.pointer(event, this)[0];
            const d = model.vis[bisect(model.vis, model.x.invert(mx))];
            if (!d) return;
            const px = model.x(d.date);
            const py = model.yPipr(d);
            cross.attr("x1", px).attr("x2", px).attr("opacity", 0.55);
            dot.attr("cx", px).attr("cy", py).attr("opacity", 1);
            place(px, py, pct(d.implied, lang, 3));
            onHover({ kind: "hist", i: d.i });
        })
        .on("mouseleave touchend", clear);

    g.append("rect")
        .attr("x", split)
        .attr("y", plotTop)
        .attr("width", fwdW)
        .attr("height", axisY - plotTop)
        .attr("fill", "transparent")
        .style("cursor", "crosshair")
        .on("mousemove touchmove", function (event: any) {
            const mx = d3.pointer(event, this)[0];
            const i = Math.max(0, Math.min(model.nF - 1, Math.floor((mx - split) / model.slot)));
            const px = model.xF(i);
            const py = model.yFwdPoint(i);
            cross.attr("x1", px).attr("x2", px).attr("opacity", 0.55);
            dot.attr("cx", px).attr("cy", py).attr("opacity", 1);
            place(px, py, pct(model.fwd.meetings[i].rateBp, lang, 2));
            onHover({ kind: "fwd", i });
        })
        .on("mouseleave touchend", clear);
}